/**
 * Formula rewriting under structural edits (pure, no DOM).
 *
 * When rows/columns are inserted or deleted — or a sheet is renamed — every
 * formula in the workbook that references the affected sheet must be adjusted
 * so it keeps pointing at the same DATA (Excel semantics):
 *
 *  · refs at/after an insertion point shift down/right
 *  · refs past a deleted span shift up/left
 *  · refs INTO a deleted span become the `#REF!` literal
 *  · ranges expand when rows/cols are inserted inside them, shrink when part
 *    of them is deleted, and become `#REF!` only when fully deleted
 *  · absolute markers (`$A$1`) and sheet prefixes (`'My Sheet'!A1`) survive
 *
 * The rewriter works on the RAW formula text with a masked regex scan (double-
 * quoted string literals are left untouched) so user formatting — spacing,
 * function names, casing — is preserved. It intentionally shares the ref
 * grammar of `formula.ts` (`parseCellRef`, quoted sheet names with `''`
 * escapes, the `#REF!` literal).
 */
import { type CellAddr, colName, parseCellRef, sheetNameKey } from "./formula";

const REF_ERROR = "#REF!";

/** A structural edit on one sheet of a workbook. */
export interface StructuralOp {
  kind: "insertRows" | "deleteRows" | "insertCols" | "deleteCols";
  /** 0-based row/col index the edit happens at. */
  at: number;
  /** How many rows/cols are inserted/deleted (≥ 1). */
  count: number;
}

const isRowOp = (op: StructuralOp) => op.kind === "insertRows" || op.kind === "deleteRows";
const isInsert = (op: StructuralOp) => op.kind === "insertRows" || op.kind === "insertCols";

/** New value of a single row/col index, or `null` when it was deleted. */
function shiftIndex(index: number, op: StructuralOp): number | null {
  if (isInsert(op)) {
    return index >= op.at ? index + op.count : index;
  }
  if (index >= op.at + op.count) {
    return index - op.count;
  }
  return index >= op.at ? null : index;
}

/**
 * New value of a range endpoint pair on the affected dimension, or `null`
 * when the whole span was deleted. Ranges clamp instead of dying when only
 * part of them is removed.
 */
function shiftSpan(from: number, to: number, op: StructuralOp): [number, number] | null {
  if (isInsert(op)) {
    return [from >= op.at ? from + op.count : from, to >= op.at ? to + op.count : to];
  }
  const end = op.at + op.count;
  const newFrom = from >= end ? from - op.count : from >= op.at ? op.at : from;
  const newTo = to >= end ? to - op.count : to >= op.at ? op.at - 1 : to;
  return newFrom > newTo ? null : [newFrom, newTo];
}

// ── Ref scanning ─────────────────────────────────────────────────────────────

/**
 * One endpoint of a ref as written (`$B$3`): absolute markers preserved.
 */
interface Endpoint {
  addr: CellAddr;
  absCol: boolean;
  absRow: boolean;
}

function parseEndpoint(text: string): Endpoint | null {
  const m = /^(\$?)[A-Za-z]{1,3}(\$?)[0-9]{1,7}$/.exec(text);
  const addr = parseCellRef(text);
  if (!m || !addr) {
    return null;
  }
  return { addr, absCol: m[1] === "$", absRow: m[2] === "$" };
}

function printEndpoint(e: Endpoint): string {
  return `${e.absCol ? "$" : ""}${colName(e.addr.col)}${e.absRow ? "$" : ""}${e.addr.row + 1}`;
}

/**
 * Spans of double-quoted string literals in a formula (masked from ref
 * rewriting). Single-quoted sheet names are skipped over so a `"` inside a
 * quoted sheet name does not open a bogus string span.
 */
function maskedSpans(src: string): [number, number][] {
  const spans: [number, number][] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === "'") {
      i++;
      while (i < src.length) {
        if (src[i] === "'") {
          if (src[i + 1] === "'") {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      continue;
    }
    if (ch === '"') {
      const start = i;
      i++;
      while (i < src.length) {
        if (src[i] === '"') {
          if (src[i + 1] === '"') {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      spans.push([start, i]);
      continue;
    }
    i++;
  }
  return spans;
}

const REF_TEXT = String.raw`\$?[A-Za-z]{1,3}\$?[0-9]{1,7}(?::\$?[A-Za-z]{1,3}\$?[0-9]{1,7})?`;

/**
 * Matches a (possibly sheet-prefixed) cell ref or range in raw formula text:
 * `A1`, `$B$2:C3`, `Sheet2!A1`, `'My Sheet'!A1:B3`. Alternative 1 (prefixed):
 * group 1 = quoted sheet name (with `''` escapes), group 2 = bare sheet name,
 * group 3 = ref text. Alternative 2 (bare ref): group 4 = ref text. The
 * lookarounds keep function names / identifiers (`SUM(`, `TRUE`) and pieces
 * of longer tokens from matching on their own; prefixed matches always start
 * at the sheet name, so a bare ref never re-matches after a `!`.
 */
const REF_PATTERN = new RegExp(
  String.raw`(?:'((?:[^']|'')+)'|(?<![A-Za-z0-9_.$])([A-Za-z_][A-Za-z0-9_.]*))!(${REF_TEXT})(?![A-Za-z0-9_.$(:!])` +
    String.raw`|(?<![A-Za-z0-9_.$:!'])(${REF_TEXT})(?![A-Za-z0-9_.$(:!])`,
  "g",
);

interface RefMatch {
  /** Sheet name as referenced (unescaped), or `undefined` for a bare ref. */
  sheet: string | undefined;
  /** The `A1[:B2]` part (no sheet prefix). */
  refText: string;
  /** The sheet prefix exactly as written (`'My Sheet'!`), or empty. */
  prefixText: string;
}

/**
 * Rewrites every cell ref/range in `formula` through `transform` (returning
 * the FULL replacement text, or `undefined` to keep the original). Double-
 * quoted string literals are untouched.
 */
function rewriteRefs(formula: string, transform: (ref: RefMatch) => string | undefined): string {
  const masked = maskedSpans(formula);
  return formula.replace(
    REF_PATTERN,
    (
      match,
      quoted: string | undefined,
      bare: string | undefined,
      prefixedRef: string | undefined,
      bareRef: string | undefined,
      offset: number,
    ) => {
      if (masked.some(([start, end]) => offset >= start && offset < end)) {
        return match;
      }
      const sheet = quoted === undefined ? bare : quoted.replaceAll("''", "'");
      const refText = prefixedRef ?? bareRef ?? "";
      const next = transform({
        sheet,
        refText,
        prefixText: match.slice(0, match.length - refText.length),
      });
      return next ?? match;
    },
  );
}

// ── Public rewriters ─────────────────────────────────────────────────────────

/**
 * Adjusts one formula for a structural edit on the sheet named `targetSheet`.
 * `formulaSheet` is the sheet the formula LIVES on (bare refs resolve there).
 * Returns the formula unchanged when nothing it references was affected.
 */
export function rewriteFormulaForStructuralOp(
  formula: string,
  op: StructuralOp,
  targetSheet: string,
  formulaSheet: string,
): string {
  const targetKey = sheetNameKey(targetSheet);
  return rewriteRefs(formula, ({ sheet, refText, prefixText }) => {
    const effectiveSheet = sheet ?? formulaSheet;
    if (sheetNameKey(effectiveSheet) !== targetKey) {
      return undefined;
    }
    const [fromText = "", toText] = refText.split(":");
    const from = parseEndpoint(fromText);
    if (!from) {
      return undefined;
    }

    if (toText === undefined) {
      // Single cell ref.
      const index = isRowOp(op) ? from.addr.row : from.addr.col;
      const next = shiftIndex(index, op);
      if (next === null) {
        return REF_ERROR;
      }
      if (next === index) {
        return undefined;
      }
      const addr: CellAddr = isRowOp(op)
        ? { row: next, col: from.addr.col }
        : { row: from.addr.row, col: next };
      return `${prefixText}${printEndpoint({ ...from, addr })}`;
    }

    // Range: shift both endpoints on the affected dimension (clamping deletes).
    const to = parseEndpoint(toText);
    if (!to) {
      return undefined;
    }
    const [lo, hi] = isRowOp(op) ? [from.addr.row, to.addr.row] : [from.addr.col, to.addr.col];
    const span = shiftSpan(Math.min(lo, hi), Math.max(lo, hi), op);
    if (span === null) {
      return REF_ERROR;
    }
    if (span[0] === Math.min(lo, hi) && span[1] === Math.max(lo, hi)) {
      return undefined;
    }
    // Preserve the written endpoint order (parseCellRef normalises anyway).
    const [newLo, newHi] = span;
    const [newFrom, newTo] = lo <= hi ? [newLo, newHi] : [newHi, newLo];
    const fromAddr: CellAddr = isRowOp(op)
      ? { row: newFrom, col: from.addr.col }
      : { row: from.addr.row, col: newFrom };
    const toAddr: CellAddr = isRowOp(op)
      ? { row: newTo, col: to.addr.col }
      : { row: to.addr.row, col: newTo };
    const range = `${printEndpoint({ ...from, addr: fromAddr })}:${printEndpoint({ ...to, addr: toAddr })}`;
    return `${prefixText}${range}`;
  });
}

/** Whether a sheet name needs `'...'` quoting when used as a ref prefix. */
function needsQuoting(name: string): boolean {
  if (!/^[A-Za-z_][A-Za-z0-9_.]*$/.test(name)) {
    return true;
  }
  // Names that would parse as something else must be quoted.
  return parseCellRef(name) !== null || /^(?:true|false)$/i.test(name);
}

/** Prints a sheet name as a formula prefix (quoting + escaping when needed). */
export function printSheetPrefix(name: string): string {
  return needsQuoting(name) ? `'${name.replaceAll("'", "''")}'!` : `${name}!`;
}

/**
 * Rewrites the sheet prefixes referencing `oldName` to `newName` (used when a
 * sheet is renamed). Bare refs are untouched — they are relative to the sheet
 * the formula lives on, whatever it is called.
 */
export function rewriteFormulaForSheetRename(
  formula: string,
  oldName: string,
  newName: string,
): string {
  const oldKey = sheetNameKey(oldName);
  return rewriteRefs(formula, ({ sheet, refText }) => {
    if (sheet === undefined || sheetNameKey(sheet) !== oldKey) {
      return undefined;
    }
    return `${printSheetPrefix(newName)}${refText}`;
  });
}
